import { useMutation, useQueryClient } from 'react-query';
import PlaylistsApi, { PlaylistContentType } from './api';

export function usePlaylistMutations() {
  const qc = useQueryClient();

  const bump = (contentId?: string, contentType?: PlaylistContentType) => {
    void qc.invalidateQueries(['playlists']);
    void qc.invalidateQueries(['playlist']);
    if (contentId && contentType) {
      void qc.invalidateQueries(['playlist-status', contentId, contentType]);
    } else {
      void qc.invalidateQueries(['playlist-status']);
    }
  };

  const createPlaylist = useMutation(PlaylistsApi.create, {
    onSuccess: () => bump(),
  });

  const updatePlaylist = useMutation(
    ({
      id,
      body,
    }: {
      id: string;
      body: Parameters<typeof PlaylistsApi.update>[1];
    }) => PlaylistsApi.update(id, body),
    { onSuccess: () => bump() },
  );

  const removePlaylist = useMutation(PlaylistsApi.remove, {
    onSuccess: () => bump(),
  });

  const addItem = useMutation(
    ({
      playlistId,
      contentId,
      contentType,
    }: {
      playlistId: string;
      contentId: string;
      contentType: PlaylistContentType;
    }) => PlaylistsApi.addItem(playlistId, { contentId, contentType }),
    { onSuccess: (_d, v) => bump(v.contentId, v.contentType) },
  );

  const removeItem = useMutation(
    ({
      playlistId,
      itemId,
      contentId,
      contentType,
    }: {
      playlistId: string;
      itemId: string;
      contentId?: string;
      contentType?: PlaylistContentType;
    }) => PlaylistsApi.removeItem(playlistId, itemId),
    { onSuccess: (_d, v) => bump(v.contentId, v.contentType) },
  );

  const reorderItems = useMutation(
    ({
      playlistId,
      itemIds,
    }: {
      playlistId: string;
      itemIds: string[];
    }) => PlaylistsApi.reorderItems(playlistId, { itemIds }),
    {
      onSuccess: (_x, v) =>
        void qc.invalidateQueries(['playlist', v.playlistId]),
    },
  );

  const updateProgress = useMutation(
    ({
      playlistId,
      body,
    }: {
      playlistId: string;
      body: Parameters<typeof PlaylistsApi.updateProgress>[1];
    }) => PlaylistsApi.updateProgress(playlistId, body),
    {
      onSuccess: (_x, v) =>
        void qc.invalidateQueries(['playlist', v.playlistId]),
    },
  );

  const toggleWatchLater = useMutation(
    async (args: {
      playlistId: string;
      contentId: string;
      contentType: PlaylistContentType;
      shouldAdd: boolean;
      itemId?: string | null;
    }) => {
      if (args.shouldAdd) {
        await PlaylistsApi.addItem(args.playlistId, {
          contentId: args.contentId,
          contentType: args.contentType,
        });
      } else {
        const wid =
          args.itemId ??
          (await PlaylistsApi.getStatus(args.contentId, args.contentType))
            .watchLaterItemId;
        if (wid)
          await PlaylistsApi.removeItem(args.playlistId, wid);
      }
    },
    {
      onSuccess: (_x, v) => bump(v.contentId, v.contentType),
    },
  );

  return {
    createPlaylist,
    updatePlaylist,
    removePlaylist,
    addItem,
    removeItem,
    reorderItems,
    updateProgress,
    toggleWatchLater,
  };
}
